import React, { createContext, useCallback, useContext, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useBillingMode } from "@/hooks/useBillingMode";
import { useAutoAcceptNetworkInvite } from "@/hooks/useAutoAcceptNetworkInvite";

type GateState = {
  open: boolean;
  canAccess: boolean;
  status: "none" | "pending";
  openGate: (e?: { preventDefault: () => void }) => void;
  closeGate: () => void;
};

const Ctx = createContext<GateState>({
  open: false,
  canAccess: true,
  status: "none",
  openGate: () => {},
  closeGate: () => {},
});

export const useLRDirectGate = () => useContext(Ctx);

export const LRDirectGateProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, profile, loading: authLoading, isAdmin, isDemoUser } = useAuth();
  const { hasAnyLRDirectAccess, isLoading: billingLoading } = useBillingMode();
  const [open, setOpen] = useState(false);

  // Phase D: auto-accept any pending shipper-network invitation once the broker
  // has a confirmed LR Direct subscription. No-op when no token is stashed.
  useAutoAcceptNetworkInvite(hasAnyLRDirectAccess && user?.id ? user.id : null);

  const isApproved = profile?.lr_direct_request_status === "approved";
  const isShipper = profile?.user_type === "shipper";
  const status: "none" | "pending" =
    profile?.lr_direct_request_status === "pending" ? "pending" : "none";
  // v3: route access is no longer gated by canAccess (LRDirectRouteGuard is
  // a pass-through; RLS handles private-load privacy). canAccess is kept for
  // back-compat and now means "should NOT see the upgrade modal" — admins,
  // demo, shippers, approved waitlist, and any paid LR Direct subscriber.
  // openGate() is invoked by BidActionGate when a broker tries to bid
  // without the required tier.
  const canAccess = isAdmin || isDemoUser || isApproved || isShipper || hasAnyLRDirectAccess;

  const openGate = useCallback(
    (e?: { preventDefault: () => void }) => {
      // Suppress the modal while auth or billing state is still resolving —
      // prevents a paid broker from being prompted to "request access" during
      // the brief window after Stripe redirect when billing-mode hasn't loaded.
      if (authLoading || billingLoading) {
        e?.preventDefault();
        return;
      }
      if (canAccess) return; // bypass — let nav happen
      e?.preventDefault();
      setOpen(true);
    },
    [canAccess, authLoading, billingLoading]
  );

  const closeGate = useCallback(() => setOpen(false), []);

  return (
    <Ctx.Provider value={{ open, canAccess, status, openGate, closeGate }}>
      {children}
    </Ctx.Provider>
  );
};

